import type { FormikProps } from "formik";
import type { Map } from "mapbox-gl";
import type React from "react";
import {
  type FC,
  type HTMLAttributes,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import {
  InvisibleInput,
  ParticipationModule,
  SurveyDemographics,
} from "senf-atomic-design-system";
import Button from "senf-atomic-design-system/src/components/core/button/Button";
import { useSocialLinks } from "senf-atomic-design-system/src/components/organisms/socialmediaShare/SocialmediaShare";
import {
  usePrepareSurveyPage,
  useRandomSorting,
} from "senf-atomic-design-system/src/components/special/survey/hooks";
import type {
  Question,
  QuestionValue,
} from "senf-atomic-design-system/src/components/special/survey/survey.types";
import { openLink, stripMarkdown, useGlobalState } from "senf-shared";
import type { Project, Survey, User } from "senf-shared/types";
import { tv } from "tailwind-variants";
import { cn } from "../../../util/classNameConstructor";
import TextToSpeech from "../../atoms/textToSpeech/TextToSpeech";
import Dropdown from "../../core/dropdown/Dropdown";
import Seperator from "../../core/seperator/Seperator";
import { useModals } from "../../molecules/modalStack/ModalProvider";
import { PageFooter } from "../infoPage/InfoPage";
import { SurveyLogos } from "./components/SortableLogos";
import ImageChoice from "./questions/ImageChoice";
import MapInput from "./questions/MapInput";
import Matrix from "./questions/Matrix";
import MultipleChoice from "./questions/MultipleChoice";
import ParticipatoryBudget from "./questions/ParticipatoryBudget";
import Ranking from "./questions/Ranking";
import Rating from "./questions/Rating";
import Text from "./questions/Text";
import Textarea from "./questions/Textarea";
import { useEditQuestion } from "./utils";

export interface SurveyQuestionProps extends Partial<Question> {
  pages: Question[];
  setPages?: React.Dispatch<React.SetStateAction<Question[]>>;
  featureId?: string;
  setFeatureId?: React.Dispatch<React.SetStateAction<string>>;
  pageIndex: number;
  outroButtons?: {
    buttonLabel: string;
    onClick: () => void;
    previewWarning?: string;
    trailingIcon?: string;
  }[];
  mode: "edit" | "display";
  formik: FormikProps<Survey>;
  demographicsFormik?: FormikProps<User>;
  required?: boolean;
  loadingImage?: boolean;
  isMobile?: boolean;
  statefulMap?: Map;
  user?: User;
  project?: Project;
  surveyId?: string;
  handleSaveDrawn?: (polygon: GeoJSON.Feature) => void;
  setOpenDescription?: (open: boolean) => void;
}

/**
 * if you need to access the defaults in a loop, don't pass
 * the page to the hook. The hook will return a function
 * which can be used in the loop.
 * */
type ReturnedDefaults = {
  defaultTitle: string;
  titlePlaceholder: string;
  defaultDescription: string;
  defaultTextTitle: string;
  defaultOtherTitle: string;
  defaultMapSelectDescription: string;
};
export function useDefaultValues(): (p: Question) => ReturnedDefaults;
export function useDefaultValues(p: Question): ReturnedDefaults;
export function useDefaultValues(p?: Question) {
  const { t } = useTranslation();

  const getDefaults = useMemo(
    () => (page: Question) => {
      const defaultTitleMap = {
        intro: "survey:builder.titleOfIntro",
        outro: "survey:builder.titleOfOutro",
        section: "survey:builder.titleOfYourSection",
        demographics: "global:demographicData",
      };
      const defaultTitle = defaultTitleMap[page?.qtype]
        ? t(defaultTitleMap[page?.qtype])
        : undefined;

      const titlePlaceholder =
        defaultTitle || t("survey:builder.titleOfYourQuestion");

      const getFeatureType = (type: string, count: number) => {
        return t(count === 1 ? `global:${type}` : `global:${type}s`);
      };

      const getChoiceType = (count: number) => {
        return t(count === 1 ? "global:option" : "global:options");
      };

      const minMaxChoiceKey = !page?.multipleChoice
        ? "youCanOnlySelectOneOption"
        : page?.minChoices &&
            page?.maxChoices &&
            page?.minChoices === page?.maxChoices
          ? "youMustSelectExactlyChoices"
          : page?.minChoices && page?.maxChoices
            ? "youMustSelectMinMaxChoices"
            : !page?.minChoices && page?.maxChoices
              ? "youMustSelectMaxChoices"
              : page?.minChoices && !page?.maxChoices
                ? "youMustSelectMinChoices"
                : "youCanSelectMultipleOptions";

      const minMaxFeatureKey = !page?.mapbasedFeatureLimits
        ? "youCanAddMultipleFeatures"
        : page?.minFeatures &&
            page?.maxFeatures &&
            page?.minFeatures === page?.maxFeatures
          ? "youMustAddExactlyFeatures"
          : page?.minFeatures && page?.maxFeatures
            ? "youMustAddMinMaxFeatures"
            : !page?.minFeatures && page?.maxFeatures
              ? "youMustAddMaxFeatures"
              : page?.minFeatures && !page?.maxFeatures
                ? "youMustAddMinFeatures"
                : "youCanAddMultipleFeatures";

      const choiceConfig = {
        count: !page?.multipleChoice
          ? 1
          : page?.minChoices || page?.maxChoices || 2,
        maxCount: page?.maxChoices,
        choiceType: getChoiceType(
          !page?.multipleChoice ? 1 : page?.maxChoices || page?.minChoices || 2,
        ),
      };
      const featureConfig = {
        count: !page?.mapbasedFeatureLimits
          ? 1
          : page?.minFeatures || page?.maxFeatures || 2,
        maxCount: page?.maxFeatures,
        featureType: getFeatureType(
          page?.qtype || "point",
          !page?.mapbasedFeatureLimits
            ? 2
            : page?.maxFeatures || page?.minFeatures || 2,
        ),
      };

      const defaultDescriptionMap = {
        radio: [`survey:options.${minMaxChoiceKey}`, choiceConfig],
        imageChoice: [`survey:options.${minMaxChoiceKey}`, choiceConfig],
        radioMatrix: [`survey:options.${minMaxChoiceKey}`, choiceConfig],
        demographics: ["auth:authOptions.demographicDataDescription"],
        point: [`survey:options.${minMaxFeatureKey}`, featureConfig],
        path: [`survey:options.${minMaxFeatureKey}`, featureConfig],
        area: [`survey:options.${minMaxFeatureKey}`, featureConfig],
        ranking: [
          `survey:options.${page?.limitRanking ? "rankingDescriptionWithLimit" : "rankingDescription"}`,
          {
            count: page?.limitRanking,
          },
        ],
        default: ["survey:builder.titleOfYourQuestion"],
      };
      const [desc1, desc2] =
        defaultDescriptionMap[page?.qtype] ||
        defaultDescriptionMap?.default ||
        [];
      let defaultDescription = t(desc1, desc2);
      if (page?.qtype === "radioMatrix") {
        defaultDescription += `, ${t("survey:options.perRow")}`;
      }

      const textPlaceholderMap = {
        radio: "survey:pleaseSpecify",
        ratingScale: "survey:elaborateBriefly",
        stars: "survey:elaborateBriefly",
        slider: "survey:elaborateBriefly",
        point: t("util:enterDescriptionFor", {
          val: t(`global:${page?.qtype}`),
        }),
        path: t("util:enterDescriptionFor", {
          val: t(`global:${page?.qtype}`),
        }),
        area: t("util:enterDescriptionFor", {
          val: t(`global:${page?.qtype}`),
        }),
        number: "survey:pleaseEnter",
        default: "survey:describeBriefly",
      };

      // We do this so that the mapbased radio other option gets the right placeholder
      const type =
        page?.mapbasedQtype &&
        ["point", "path", "area"].includes(page?.qtype) &&
        ["radio"].includes(page?.mapbasedQtype)
          ? "radio"
          : page?.qtype;

      const defaultTextTitle = t(
        textPlaceholderMap[type] || textPlaceholderMap?.default,
      );
      const defaultOtherTitle = t("global:other");
      const defaultMapSelectDescription = t("survey:selectOption");

      return {
        defaultTitle,
        titlePlaceholder,
        defaultDescription,
        defaultTextTitle,
        defaultOtherTitle,
        defaultMapSelectDescription,
      };
    },
    [
      t,
      p?.minChoices,
      p?.maxChoices,
      p?.minFeatures,
      p?.maxFeatures,
      p?.mapbasedQtype,
      p?.qtype,
      p?.multipleChoice,
      p?.mapbasedFeatureLimits,
      p?.limitRanking,
    ],
  );

  if (!p) return getDefaults;

  return useMemo(() => getDefaults(p), [getDefaults, p]);
}

interface SocialShareProps extends HTMLAttributes<HTMLDivElement> {
  previewLink: string;
}

const SocialShare: FC<SocialShareProps> = ({ className, previewLink }) => {
  const { socialLinks } = useSocialLinks();
  return (
    <div className={cn("flex gap-2", className)}>
      {socialLinks({
        path: previewLink,
        trackLocation: "shareSurvey",
        filterOut: ["qr-code"] as const,
      }).map(({ leadingIcon, onClick }, i) => (
        <Button
          variant="outline"
          size="xl"
          leadingIcon={leadingIcon}
          onClick={onClick}
          key={`social-${i}`}
        />
      ))}
    </div>
  );
};

const OutroButton = ({
  buttonLabel,
  previewWarning,
  buttonUrl,
  onClick,
  trailingIcon,
  i,
}) => {
  const { t } = useTranslation();
  const [translation] = useGlobalState().translationsState;
  const { openModal } = useModals();
  const handleClick = () => {
    if (previewWarning) {
      openModal(null, {
        id: buttonLabel,
        title: t("global:previewModeWarning.title"),
        description: t("global:previewModeWarning.description"),
        zIndex: 999999,
      });
    } else if (onClick) {
      onClick();
    } else if (buttonUrl) {
      openLink(buttonUrl);
    }
  };
  return (
    <Button
      variant="basic"
      size="xl"
      text={translation?.[buttonLabel] || buttonLabel}
      onClick={handleClick}
      trailingIcon={trailingIcon || "arrowhead-right-md"}
      classNameWrapper="shrink"
      key={i}
    />
  );
};
const Outro = ({ outroButtons, page, className }) => {
  const [translation] = useGlobalState().translationsState;

  const buttons = [...(outroButtons || [])];

  const addPostButtons = buttons.filter((button) => button.type === "addPost");
  const otherButtons = buttons.filter((button) => button.type !== "addPost");

  return (
    <div className={cn("mt-2 flex flex-col gap-2", className)}>
      {otherButtons.map((props, i) => (
        <OutroButton
          {...props}
          i={i}
          key={props.buttonLabel}
          trailingIcon="arrowhead-right-md"
        />
      ))}

      {addPostButtons.length > 1 ? (
        <Dropdown
          variant="basic"
          size="xl"
          trailingIcon="arrowhead-right-md"
          text={
            translation?.[addPostButtons[0]?.buttonLabel] ||
            addPostButtons[0]?.buttonLabel
          }
          items={addPostButtons.map((item, index) => {
            return {
              text: item?.ideaMapTitle,
              onClick: item?.onClick,
            };
          })}
        />
      ) : (
        addPostButtons.map((props, i) => (
          <OutroButton
            {...props}
            i={i}
            key={props.buttonLabel}
            trailingIcon="arrowhead-right-md"
          />
        ))
      )}

      {page?.qbuttons?.map((props, i) => (
        <OutroButton {...props} i={i} key={props.buttonLabel} />
      ))}
    </div>
  );
};

const SurveyQuestion: FC<SurveyQuestionProps> = (props) => {
  const [accessibility] = useGlobalState().accessibilityState;

  const {
    pageIndex,
    pages,
    outroButtons,
    loadingImage,
    isMobile,
    mode,
    formik,
    statefulMap,
    demographicsFormik,
    user,
    surveyId,
    project,
    handleSaveDrawn,
  } = props;
  const { t } = useTranslation();
  const {
    currentPage: page,
    currentPageAlignment,
    currentPageIsMapbased,
    currentPageHasImage,
    currentPageRole,
  } = usePrepareSurveyPage({ pages, index: pageIndex, isMobile });

  const pageAlignment =
    !loadingImage || isMobile ? currentPageAlignment : undefined;

  const alignmentClass = {
    left: "items-start justify-start",
    center: "items-center justify-center",
  }[pageAlignment || "left"];

  const handleOnChange = (
    { value, ...rest }: Partial<QuestionValue> = {},
    onLoad = false,
  ) => {
    formik.setFieldValue(page.uid, {
      ...formik.values[page.uid],
      ...rest,
      value:
        value === undefined ? formik.values[page.uid]?.value || null : value,
      timeStamp:
        (!onLoad && new Date().toISOString()) ||
        formik.values[page.uid]?.timeStamp ||
        null,
    });
  };

  useEffect(() => {
    if (
      currentPageRole === "surveyPage" ||
      currentPageRole === "demographics"
    ) {
      // initiate to set the value of the current page to the formik values
      handleOnChange({}, true);
    }
  }, [pageIndex]);

  const {
    updateQuestion,
    updateOption,
    addItem,
    removeItem,
    updateText,
    generateItems,
  } = useEditQuestion(props);

  const {
    defaultTitle,
    titlePlaceholder,
    defaultDescription,
    defaultTextTitle,
    defaultOtherTitle,
  } = useDefaultValues(page);

  if (!page) return null;

  const baseProps = {
    ...formik?.values[page.uid],
    ...page,
    page,
    formikValues: formik?.values[page.uid],
    mode,
    onChange: handleOnChange,
    updateQuestion,
    removeItem,
    addItem,
    updateText,
    generateItems,
    defaultTextTitle,
    defaultOtherTitle,
    currentPage: page,
    isMobile,
  };

  const [translation] = useGlobalState().translationsState;
  const { stack } = useModals();

  const previewLink =
    import.meta.env.VITE_APP_MODE === "development"
      ? `http://localhost:3000/projects/${project?.projectId}/survey/${surveyId}`
      : import.meta.env.VITE_APP_MODE === "production"
        ? `https://senf.app/projects/${project?.projectId}/survey/${surveyId}`
        : `https://demo.senf.app/projects/${project?.projectId}/survey/${surveyId}`;
  const titleClassName = cn(
    mode === "edit"
      ? "max-w-[min(720px,100%)]"
      : "max-w-[min(calc(720px-2.5rem-1.25rem),100%-2.5rem)]",
    isMobile && pageAlignment === "center" && "text-balance",
    isMobile && currentPageRole === "intro" && currentPageHasImage && "px-0",
    isMobile &&
      currentPageRole !== "intro" &&
      pageAlignment !== "center" &&
      "max-w-[calc(100%-1rem)]",
  );
  const legendClassName = cn(
    isMobile && !(currentPageRole === "intro" && currentPageHasImage)
      ? pageAlignment === "center"
        ? "px-12"
        : "pe-12"
      : "",
  );

  const fieldsetRef = useRef<HTMLDivElement & HTMLFieldSetElement>(null);

  // useEffect(() => {
  //   if (!fieldsetRef.current || mode === "edit") return;

  //   const introFocus = document.getElementById("take-survey-continue-button");

  //   const tabIndexZero = document.querySelectorAll('[tabindex="0"]');
  //   const focused = document.querySelector(":focus");
  //   const focusedVisible = document.querySelector(":focus-visible");

  //   if (pageIndex === 0) {
  //     // for (const element of tabIndexZero) element.setAttribute("tabindex", "2");
  //     // introFocus?.focus()
  //     // introFocus?.setAttribute("tabindex", "1");
  //   } else {
  //     // for (const element of tabIndexZero) element.setAttribute("tabindex", "0");
  //     // introFocus?.removeAttribute("tabindex");
  //   }

  //   const focusable =
  //     pageIndex > 0
  //       ? fieldsetRef.current.querySelector<HTMLElement>(
  //           ':is(button, [href], input, select, textarea, [role="slider"], [tabindex]):not([tabindex="-1"])',
  //         )
  //       : introFocus;
  //   setTimeout(() => {
  //     focusable?.focus();
  //   });

  //   const scrollIntoView = (event) => {
  //     const el = (event.target as HTMLElement)?.closest("label");
  //     if (el) {
  //       el.scrollIntoView({
  //         behavior: "smooth",
  //         block: "nearest",
  //         inline: "center",
  //       });
  //     }
  //   };
  //   fieldsetRef.current?.addEventListener("focusin", scrollIntoView);
  //   return () => {
  //     fieldsetRef.current?.removeEventListener("focusin", scrollIntoView);
  //   };
  // }, [mode, pageIndex, stack.length]);

  // fieldset has quirky display behaviour so in edit mode we use a div instead
  const FieldSet = mode === "display" ? "fieldset" : "div";
  // const sortedOptions = useRandomSorting(page, "options", mode);
  // biome-ignore lint/correctness/useExhaustiveDependencies: we need to update the translations upon page change
  const tts: string = useMemo(() => {
    const base = [
      translation?.[page.qtext] || page.qtext || defaultTitle,
      (page.qbody && translation?.[page.qbody]) ||
        page.qbody ||
        defaultDescription,
    ];

    // if (currentPageRole === "intro") {
    //   tts.push("Starte die Umfrage mit Klick auf den Button");
    // } else if (currentPageRole === "demographics") {
    //   tts.push(
    //     "Fülle bitte die folgenden Felder aus",
    //     "Postleitzahl, Alter, Geschlecht",
    //   );
    // } else if (currentPageRole === "surveyPage") {
    //   if (page.qtype === "text") {
    //     tts.push(
    //       `Schreibe deine Antwort in das ${sortedOptions?.length === 1 ? "das" : `die ${sortedOptions?.length}`} Textfeld.`,
    //     );
    //   } else if (
    //     page.qtype === "radio" ||
    //     page.qtype === "imageChoice" ||
    //     page.qtype === "radioMatrix"
    //   ) {
    //     tts.push(
    //       ...(sortedOptions || []).map(
    //         (option, index) =>
    //           `${index + 1}. option: ${translation?.[option.value] || option.value}`,
    //       ),
    //     );
    //   } else if (
    //     page.qtype === "point" ||
    //     page.qtype === "path" ||
    //     page.qtype === "area"
    //   ) {
    //     tts.push("Wähle eine Option aus.");
    //   } else if (page.qtype === "ranking") {
    //     tts.push("Wähle eine Option aus.");
    //   } else if (page.qtype === "budget") {
    //     tts.push("Wähle eine Option aus.");
    //   }
    //   if (page.otherOption) {
    //     tts.push(
    //       `${sortedOptions?.length}. option: ${translation?.[page.otherTitle] || page.otherTitle || t("global:other")}`,
    //     );
    //   }
    // }
    return stripMarkdown(base.join(". ").concat("."));
  }, [pageIndex, translation, page, defaultTitle, defaultDescription]);

  return (
    <div className="flex flex-col gap-2 @container/page">
      {accessibility && (
        <TextToSpeech
          id="surveyQuestion:ttsPlayClick"
          className={cn("mb-1 -mx-1", alignmentClass)}
          text={tts}
        />
      )}
      <FieldSet
        className={cn(
          "flex w-full flex-col p-0",
          isMobile ? (currentPageIsMapbased ? "gap-0 pb-0" : "") : "",
          project &&
            currentPageRole === "intro" &&
            mode === "display" &&
            "pb-20 @xl/page:pb-10",
        )}
        key={pageIndex}
        ref={fieldsetRef}
        aria-required={page.required}
        aria-describedby="question-description"
      >
        {mode === "display" ? (
          <legend
            className={cn(
              "heading-3xl @xl/page:text-4xl py-[0.5em] hyphens-auto",
              pageAlignment === "center" ? "text-center mx-auto" : "",
              titleClassName,
              legendClassName,
            )}
            id="question-legend"
          >
            {translation?.[page.qtext] || page.qtext || defaultTitle}
          </legend>
        ) : (
          <InvisibleInput
            id="pageTitle"
            size="heading-xl"
            as="h1"
            value={translation?.[page.qtext] || page.qtext}
            defaultValue={defaultTitle}
            onChange={(e) => updateQuestion(e.currentTarget.value, "qtext")}
            placeholder={titlePlaceholder}
            maxRows={8}
            textAlign={pageAlignment}
            mode={mode}
            classNames={{
              wrapper: cn(legendClassName, titleClassName),
            }}
          />
        )}

        <InvisibleInput
          id="pageDescription"
          size="body-md"
          markdown={true}
          value={translation?.[page.qbody] || page.qbody}
          defaultValue={
            page.qtype === "radio" ||
            page.qtype === "imageChoice" ||
            page.qtype === "radioMatrix" ||
            page.qtype === "demographics" ||
            page.qtype === "point" ||
            page.qtype === "path" ||
            page.qtype === "area" ||
            page.qtype === "ranking"
              ? defaultDescription
              : undefined
          }
          onChange={(e) => updateQuestion(e.target.value, "qbody")}
          maxRows={18}
          placeholder={t("global:description")}
          textAlign={pageAlignment}
          mode={mode}
          classNames={{
            wrapper: cn(
              titleClassName,
              pageAlignment === "center" ? "mx-auto" : "",
            ),
          }}
          // markdown={true}
        />
        <div
          className={cn(
            "flex flex-col gap-5 my-5",
            isMobile && !currentPageIsMapbased && "pb-56",
          )}
        >
          {(currentPageRole === "intro" || currentPageRole === "outro") &&
          page?.logo?.length ? (
            <SurveyLogos
              page={page}
              setPages={props.setPages}
              formik={formik}
              project={project}
              surveyId={surveyId}
              mode={mode}
              pageAlignment={pageAlignment}
              updateQuestion={updateQuestion}
              removeItem={removeItem}
              addItem={addItem}
              updateOption={updateOption}
            />
          ) : null}

          {currentPageIsMapbased && (
            <MapInput
              {...baseProps}
              formik={formik}
              isMobile={isMobile}
              statefulMap={statefulMap}
              featureId={props.featureId}
              setFeatureId={props.setFeatureId}
              setOpenDescription={props?.setOpenDescription}
              handleSaveDrawn={handleSaveDrawn}
            />
          )}
          {page.qtype === "radioMatrix" && (
            <Matrix {...baseProps} formik={formik} isMobile={isMobile} />
          )}
          {page.qtype === "ranking" && <Ranking {...baseProps} />}

          {!currentPageIsMapbased && page.qtype === "radio" && (
            <MultipleChoice {...baseProps} />
          )}
          {!currentPageIsMapbased && page.qtype === "budget" && (
            <ParticipatoryBudget {...baseProps} />
          )}

          {!currentPageIsMapbased && page.qtype === "imageChoice" && (
            <ImageChoice
              {...baseProps}
              project={props.project}
              surveyId={props.surveyId}
              updateOption={updateOption}
            />
          )}
          {page.qtype === "demographics" && (
            <SurveyDemographics
              {...baseProps}
              demographicsFormik={demographicsFormik}
              project={props.project}
              user={user}
            />
          )}
          {!currentPageIsMapbased &&
            (page.qtype === "ratingScale" ||
              page.qtype === "stars" ||
              page.qtype === "slider") && <Rating {...baseProps} />}

          {page.qtype === "text" && (
            <Text
              {...baseProps}
              onChange={(value) => handleOnChange({ value })}
              textRequired={page.required}
              defaultTextTitle={defaultTextTitle}
            />
          )}
          {page.qtype === "number" && (
            <Text
              {...baseProps}
              type="number"
              onChange={(value) => handleOnChange({ value })}
              textRequired={page.required}
              min={page.minValue ?? 0}
              max={page.maxValue ?? 999999999}
              defaultTextTitle={defaultTextTitle}
            />
          )}
          {page?.qsocialShare && (
            <div className="flex flex-col gap-3">
              <span
                className={cn(
                  "body-md-bold",
                  pageAlignment === "center" ? "text-center" : "",
                )}
              >
                {t("survey:shareOnSocialMedia")}:
              </span>
              <SocialShare
                className={alignmentClass}
                previewLink={previewLink}
              />
            </div>
          )}
          {currentPageRole === "outro" && (
            <Outro
              outroButtons={outroButtons}
              page={page}
              className={alignmentClass}
            />
          )}

          {project && currentPageRole === "intro" && mode === "display" && (
            <div
              className={cn(
                !isMobile &&
                  "fixed h-[var(--h-screen,100vh)] top-[calc((100vh-var(--h-screen,100vh))/2)] flex justify-center items-end pointer-events-none duration-500 ease-expressive",
                !isMobile &&
                  currentPageHasImage &&
                  "start-[calc(var(--w-screen,100vw)/2)] w-[calc(var(--w-screen,100vw)/2)] transition-[top,bottom,height,width]",
                !isMobile &&
                  !currentPageHasImage &&
                  "start-[calc(50vw-var(--w-screen,100vw)/2)] w-[var(--w-screen,100vw)] transition-[inset,height,width]",
              )}
            >
              <div
                className={cn(
                  !isMobile &&
                    "flex mb-5 flex-wrap items-center h-11 w-full mx-auto px-10 group-[.has-image:not(.cover)]:ps-5 duration-500 ease-expressive",
                  !isMobile &&
                    currentPageHasImage &&
                    "max-w-[720px] mb-20 @xl/page:mb-5",
                  // !isMobile && pageAlignment && "ms-10",
                )}
              >
                {isMobile && <Seperator className="p-10" />}
                <footer
                  className={cn(
                    "flex gap-3 pointer-events-auto bg-white -mx-5 -my-3 px-5 py-3 rounded-full",
                    isMobile && "justify-center",
                  )}
                >
                  <PageFooter project={project} />
                </footer>
              </div>
            </div>
          )}
        </div>
      </FieldSet>
    </div>
  );
};

export default SurveyQuestion;
